import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowRight, CalendarClock, CalendarDays, CheckCircle2, ChevronRight, Clock3, CloudSun, LockKeyhole, MapPin, Plane, Plus, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { NewTripWizard } from "../../components/NewTripWizard";
import { TripCard, TripSkeleton } from "../../components/TripCard";
import { api, daysUntil } from "../../lib/api";
import { destinationImage } from "../../lib/travel";
import type { Trip, TripWeather, WishlistItem } from "../../lib/types";
import { useAuth } from "../auth/auth-context";

function parseDate(value: string | null) {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function dateKey(value: Date) {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
}

function preferredCalendarMonth(trips: Trip[]) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dated = trips
    .filter((trip) => trip.start_date && !["cancelled", "archived"].includes(trip.status))
    .map((trip) => ({ trip, start: parseDate(trip.start_date)!, end: parseDate(trip.end_date || trip.start_date)! }))
    .sort((left, right) => left.start.getTime() - right.start.getTime());
  const relevant = dated.find(({ end }) => end >= today) || dated.at(-1);
  const anchor = relevant?.start || today;
  return new Date(anchor.getFullYear(), anchor.getMonth(), 1);
}

export function TravelCalendar({ trips }: { trips: Trip[] }) {
  const [viewMonth, setViewMonth] = useState(() => preferredCalendarMonth(trips));
  const tripSignature = trips.map((trip) => `${trip.id}:${trip.start_date}:${trip.end_date}:${trip.status}`).join("|");
  useEffect(() => setViewMonth(preferredCalendarMonth(trips)), [tripSignature, trips]);
  const activeTrips = useMemo(() => trips.filter((trip) => trip.start_date && !["cancelled", "archived"].includes(trip.status)), [trips]);
  const monthStart = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
  const monthEnd = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0);
  const visibleTrips = activeTrips.filter((trip) => {
    const start = parseDate(trip.start_date)!;
    const end = parseDate(trip.end_date || trip.start_date)!;
    return start <= monthEnd && end >= monthStart;
  });
  const daysInMonth = monthEnd.getDate();
  const firstWeekday = monthStart.getDay();
  const moveMonth = (offset: number) => setViewMonth((current) => new Date(current.getFullYear(), current.getMonth() + offset, 1));

  return <article className="insight-card calendar-card"><div className="card-title"><span><CalendarDays size={19} /></span><div><p className="eyebrow">TRAVEL CALENDAR</p><h3>Your year in motion</h3></div></div><div className="mini-calendar"><div className="month-row"><button aria-label="Previous month" onClick={() => moveMonth(-1)}>‹</button><strong>{viewMonth.toLocaleDateString("en", { month: "long", year: "numeric" })}</strong><button aria-label="Next month" onClick={() => moveMonth(1)}>›</button></div><div className="calendar-days">{"SMTWTFS".split("").map((day, index) => <small key={`${day}-${index}`}>{day}</small>)}{Array.from({ length: firstWeekday }, (_, index) => <span className="calendar-empty" aria-hidden="true" key={`empty-${index}`} />)}{Array.from({ length: daysInMonth }, (_, index) => {
    const date = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), index + 1);
    const key = dateKey(date);
    const coveringTrips = visibleTrips.filter((trip) => trip.start_date! <= key && (trip.end_date || trip.start_date)! >= key);
    const classNames = [coveringTrips.length ? "trip-date" : "", coveringTrips.some((trip) => trip.start_date === key) ? "trip-start" : "", coveringTrips.some((trip) => (trip.end_date || trip.start_date) === key) ? "trip-end" : ""].filter(Boolean).join(" ");
    return <span key={key} className={classNames} title={coveringTrips.map((trip) => trip.title).join(", ") || undefined}>{index + 1}</span>;
  })}</div></div><div className="calendar-events">{visibleTrips.length ? visibleTrips.map((trip) => {
    const start = parseDate(trip.start_date)!; const end = parseDate(trip.end_date || trip.start_date)!;
    const days = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1);
    return <Link className="calendar-event" to={`/trips/${trip.id}/overview`} key={trip.id}><i /><div><strong>{trip.title}</strong><span>{start.toLocaleDateString("en", { day: "numeric", month: "short" })}–{end.toLocaleDateString("en", { day: "numeric", month: "short" })} · {days} {days === 1 ? "day" : "days"}</span></div><ChevronRight size={15} /></Link>;
  }) : <div className="calendar-empty-state"><span>No journeys in this month.</span><Link to="/trips">Open travel library</Link></div>}</div></article>;
}

const wishlistPriority: Record<WishlistItem["priority"], number> = {
  must_go: 4,
  high: 3,
  medium: 2,
  low: 1,
};

function wishlistMeta(item: WishlistItem) {
  const details = [
    item.ideal_season ? `BEST · ${item.ideal_season.toUpperCase()}` : null,
    item.duration_days ? `${item.duration_days} ${item.duration_days === 1 ? "DAY" : "DAYS"}` : null,
  ].filter(Boolean);

  if (details.length) return details.join(" · ");
  return {
    must_go: "MUST GO",
    high: "HIGH PRIORITY",
    medium: "SOMEDAY",
    low: "CURIOUS",
  }[item.priority];
}

export function WishlistInsight({ items, isLoading = false, isError = false }: { items: WishlistItem[]; isLoading?: boolean; isError?: boolean }) {
  const featured = items.reduce<WishlistItem | undefined>((best, item) => (
    !best || wishlistPriority[item.priority] > wishlistPriority[best.priority] ? item : best
  ), undefined);

  const content = isLoading
    ? {
        eyebrow: "YOUR WISHLIST",
        heading: "Opening your saved places…",
        body: "Finding the destination at the top of your private shortlist.",
        meta: "LOADING",
        label: "Open wishlist",
      }
    : isError
      ? {
          eyebrow: "YOUR WISHLIST",
          heading: "Your saved places are still waiting.",
          body: "We couldn’t load them here just now. Open Explore to try again.",
          meta: "PRIVATE SHORTLIST",
          label: "Open wishlist and try again",
        }
      : featured
        ? {
            eyebrow: "FROM YOUR WISHLIST",
            heading: featured.destination.split(",")[0],
            body: featured.why || "A place you saved for the right moment.",
            meta: wishlistMeta(featured),
            label: `Open ${featured.destination} in wishlist`,
          }
        : {
            eyebrow: "YOUR WISHLIST",
            heading: "Save a place for someday.",
            body: "Explore destinations and keep the ones you want to return to—no dates required.",
            meta: "START YOUR PRIVATE SHORTLIST",
            label: "Start your wishlist",
          };

  return <article className={`insight-card idea-card${!featured ? " idea-card-empty" : ""}`} aria-busy={isLoading || undefined}>
    <div className="idea-photo" aria-hidden="true" />
    <div className="idea-content">
      <p className="eyebrow">{content.eyebrow}</p>
      <h3>{content.heading}</h3>
      <p>{content.body}</p>
      <div><span>{content.meta}</span><Link className="idea-link" to="/explore" aria-label={content.label}><ArrowRight size={18} /></Link></div>
    </div>
  </article>;
}

export function DashboardPage() {
  const { user } = useAuth();
  const [wizard, setWizard] = useState(false);
  const { data: trips = [], isLoading, isError, refetch } = useQuery({ queryKey: ["trips"], queryFn: () => api<Trip[]>("/trips"), staleTime: 0, refetchOnMount: "always" });
  const { data: wishlist = [], isLoading: wishlistLoading, isError: wishlistError } = useQuery({
    queryKey: ["wishlist"],
    queryFn: () => api<WishlistItem[]>("/wishlist"),
    staleTime: 60_000,
  });
  const next = trips.find((trip) => ["planning", "booked", "ready", "active"].includes(trip.status));
  const hasDates = Boolean(next?.start_date && next?.end_date);
  const { data: weather } = useQuery({ queryKey: ["trip-weather", next?.id], queryFn: () => api<TripWeather>(`/trips/${next?.id}/weather`), enabled: Boolean(next?.id) && hasDates, staleTime: 30 * 60 * 1000, retry: 1 });
  const countdown = daysUntil(next?.start_date || null);
  const tripForecast = weather?.daily.find((day) => day.date === next?.start_date);
  const weatherSummary = tripForecast
    ? `${tripForecast.label} · ${Math.round(tripForecast.temperature_min_c)}–${Math.round(tripForecast.temperature_max_c)}°C ${weather?.status === "seasonal" ? "seasonal outlook" : "forecast"}`
    : next?.start_date
      ? weather?.message || "Forecast not available for the planned dates yet"
      : "Set dates to unlock trip weather";
  const greeting = new Date().getHours() < 12 ? "Good morning" : new Date().getHours() < 18 ? "Good afternoon" : "Good evening";
  return <div className="dashboard page-width">
    <div className="welcome-row"><div><p className="eyebrow">{greeting}, {user?.full_name.split(" ")[0]}</p><h1>Where shall we go next?</h1></div><button className="button soft desktop-create" onClick={() => setWizard(true)}><Plus size={17} />Create a journey</button></div>
    {isError ? <div className="error-state"><p>We couldn’t reach your travel library.</p><button onClick={() => refetch()}>Try again</button></div> : next ? <motion.section className="journey-hero" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
      <img src={destinationImage(next)} alt={`View of ${next.destination}`} />
      <div className="hero-overlay" />
      <div className="hero-copy"><p className="hero-kicker">YOUR NEXT JOURNEY</p><h2>{next.destination.split("•")[0]}</h2><p className="hero-route"><MapPin size={15} />{next.destination}</p><div className="hero-stats"><div><strong>{countdown ?? next.duration_days}</strong><span>{hasDates ? "days to go" : "days planned"}</span></div><div className="hero-stat-rule" /><div className="readiness-mini"><strong>{next.readiness_score}%</strong><span>ready</span></div></div><div className="next-action"><span className="next-icon">{hasDates ? <Plane size={18} /> : <CalendarClock size={18} />}</span><div><small>NEXT ACTION</small><strong>{!hasDates ? "Choose travel dates when ready" : next.readiness_score < 80 ? "Review your trip preparation" : "Everything important is in place"}</strong></div></div><Link className="button light" to={`/trips/${next.id}/overview`}>Continue planning<ArrowRight size={18} /></Link></div>
      {hasDates ? <a className="hero-weather" href={weather?.source_url || "https://open-meteo.com/"} target="_blank" rel="noreferrer" title="Weather source"><CloudSun size={17} /><span>{weatherSummary}</span></a> : <Link className="hero-weather" to={`/trips/${next.id}/overview?settings=1`}><CalendarClock size={17} /><span>{weatherSummary}</span></Link>}
    </motion.section> : !isLoading ? <section className="empty-hero"><div><Sparkles /></div><p className="eyebrow">A BLANK MAP IS AN INVITATION</p><h2>Your next story starts here.</h2><p>Bring a destination, a season, or just a feeling. Rihvo.fun will help you turn it into a journey that fits your life.</p><button className="button primary" onClick={() => setWizard(true)}>Plan your first trip<ArrowRight size={18} /></button></section> : <div className="hero-loading skeleton" />}

    <section className="dashboard-section"><div className="section-heading"><div><p className="eyebrow">MY TRAVEL</p><h2>Journeys on the horizon</h2></div><Link to="/trips">View all<ChevronRight size={16} /></Link></div><div className="trip-grid">{isLoading ? <><TripSkeleton /><TripSkeleton /><TripSkeleton /></> : trips.length ? trips.slice(0, 3).map((trip) => <TripCard trip={trip} key={trip.id} />) : <button className="new-trip-card" onClick={() => setWizard(true)}><Plus /><strong>Create a journey</strong><span>Turn a travel idea into a living plan</span></button>}</div></section>

    <section className="insight-grid">
      <TravelCalendar trips={trips} />
      <article className="insight-card safe-card"><div className="card-title"><span><LockKeyhole size={19} /></span><div><p className="eyebrow">BOOKING SAFE</p><h3>Everything important, protected</h3></div></div><div className="safe-count"><strong>{next ? 6 : 0}</strong><span>travel records</span></div><ul><li><Plane size={16} />Flights and boarding details<CheckCircle2 size={16} /></li><li><MapPin size={16} />Stays and confirmations<CheckCircle2 size={16} /></li><li><Clock3 size={16} />Deadlines surfaced early<CheckCircle2 size={16} /></li></ul><Link to={next ? `/trips/${next.id}/bookings` : "/booking-safe"}>Open Booking Safe<ArrowRight size={16} /></Link></article>
      <WishlistInsight items={wishlist} isLoading={wishlistLoading} isError={wishlistError} />
    </section>
    {wizard && <NewTripWizard onClose={() => setWizard(false)} />}
  </div>;
}
